/**
 * Data hooks for Brand Health (logged-in merchant) and Courier Health (all
 * couriers), composed from the 6 CT freightsnapshot endpoints.
 *
 * Fetching/caching is handled by RTK Query (`useGetHealthSnapshotsQuery`, keyed by
 * shipmentType) — cached, deduped and shared across both pages (60-min TTL). This
 * module only composes the cached responses into the matrix shape.
 *
 * ⚠️ PERF NOTE: these endpoints return ALL merchants' groups and ignore
 * `merchant_list`, so the response is large and filtered client-side to the
 * logged-in merchant. A merchant-scoped CT variant would make this much lighter —
 * see docs/NEW_APIS_NEEDED.md.
 */
import {useMemo} from 'react'
import {getMerchant} from '../../helpers/ReduxHelpers'
import {useGetCouriersQuery} from '../../redux/commonRTKPersist'
import {useGetHealthSnapshotsQuery} from '../../redux/commonRTK'
import {buildBrandHealth, buildCourierHealth} from './buildHealth'

export function useBrandHealth(shipmentType = 'B2C') {
  const merchant = getMerchant()
  const {data: couriers = []} = useGetCouriersQuery()
  const {data: resp, isLoading, error} = useGetHealthSnapshotsQuery(shipmentType)
  const data = useMemo(
    () => (resp ? buildBrandHealth(resp, merchant, couriers) : null),
    [resp, couriers, merchant?.id], // eslint-disable-line react-hooks/exhaustive-deps
  )
  return {data, loading: isLoading, error}
}

export function useCourierHealth(shipmentType = 'B2C') {
  const merchant = getMerchant()
  const {data: couriers = []} = useGetCouriersQuery()
  const {data: resp, isLoading, error} = useGetHealthSnapshotsQuery(shipmentType)
  const data = useMemo(
    () => (resp ? buildCourierHealth(resp, merchant, couriers) : null),
    [resp, couriers, merchant?.id], // eslint-disable-line react-hooks/exhaustive-deps
  )
  return {data, loading: isLoading, error}
}
